import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useConsoleRole } from '../../session/SessionContext';
import { TUTORIALS, type GuideData } from '../../content/guideData';
import { sx } from '../../ui/designStyles';
import { crossRoleSearch, guideNames, pickerCard } from './guideVals';

/**
 * "Who are you?" (design noRole). Shown only to Super Admin and Dev, who may read any
 * role's guide. "Continue as" names the signed-in person's own guide (the handoff: the
 * signed-in user's actual role, not a remembered one). The search runs over every role's
 * answers; each hit opens the console page it names.
 */
const S = {
  wrap: 'min-height:100vh;box-sizing:border-box;padding:16px 28px 28px;display:flex;justify-content:center;',
  col: 'width:100%;max-width:1180px;display:flex;flex-direction:column;gap:26px;',
  top: 'display:flex;align-items:center;gap:10px 14px;flex-wrap:wrap;',
  logo: 'font-size:20px;font-weight:800;letter-spacing:-.02em;',
  kick: 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#2F5C97;font-weight:700;',
  back: 'display:inline-flex;align-items:center;min-height:32px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;color:#3C454F;border:1px solid rgba(34,39,46,.18);white-space:nowrap;',
  hero: 'display:flex;flex-direction:column;align-items:center;text-align:center;padding:26px 0 6px;animation:gRiseA .45s cubic-bezier(.22,.61,.36,1) both;',
  h: 'font-size:44px;font-weight:800;letter-spacing:-.034em;line-height:1.05;',
  sub: 'font-size:17px;color:#3C454F;margin-top:10px;max-width:560px;text-wrap:pretty;',
  last: 'display:inline-flex;align-items:center;gap:8px;min-height:44px;margin-top:18px;padding:0 20px;border-radius:10px;background:#2F5C97;color:#fff;font-size:16px;font-weight:700;text-decoration:none;',
  searchWrap: 'width:100%;max-width:640px;margin-top:20px;position:relative;text-align:left;',
  box: 'display:flex;align-items:center;gap:10px;height:54px;padding:0 16px;border-radius:12px;border:1.5px solid rgba(34,39,46,.22);background:#FCFAF4;',
  input: 'flex:1 1 auto;min-width:0;border:0;outline:0;background:transparent;font:inherit;font-size:16px;color:#22272E;height:100%;',
  clear: 'font-size:16px;font-weight:600;color:#54606C;text-decoration:none;',
  res: 'margin-top:8px;border:1px solid rgba(34,39,46,.16);border-radius:12px;background:#FCFAF4;overflow:hidden;',
  resHead: 'padding:8px 14px;font-size:14px;letter-spacing:.09em;text-transform:uppercase;font-weight:700;color:#54606C;border-bottom:1px solid rgba(34,39,46,.1);',
  hit: 'display:flex;flex-direction:column;gap:3px;padding:10px 14px;border-bottom:1px solid rgba(34,39,46,.08);text-decoration:none;color:#22272E;',
  hitTop: 'display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;',
  none: 'padding:12px 14px;font-size:16px;color:#3C454F;',
  sec: (d: number) => `display:flex;flex-direction:column;gap:12px;animation:gRiseB .5s ${d}s cubic-bezier(.22,.61,.36,1) both;`,
  secHead: (c: string) => `display:flex;align-items:baseline;gap:10px;border-top:2px solid ${c};padding-top:10px;`,
  secK: (c: string) => `font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:${c};font-weight:800;`,
  secN: 'font-size:16px;color:#54606C;',
  grid: 'display:grid;grid-template-columns:repeat(auto-fit,minmax(250px,1fr));gap:12px;',
  card: 'display:flex;flex-direction:column;gap:6px;min-height:128px;box-sizing:border-box;padding:16px 18px;border-radius:12px;border:1px solid rgba(34,39,46,.18);text-decoration:none;color:#22272E;transition:border-color .2s,background .2s,transform .2s;',
  cardH: 'display:flex;flex-direction:column;gap:6px;min-height:128px;box-sizing:border-box;padding:18px 20px;border-radius:12px;border:2px solid #1F6B52;background:rgba(47,138,111,.06);text-decoration:none;color:#22272E;transition:border-color .2s,background .2s,transform .2s;',
  foot: 'font-size:16px;color:#54606C;padding-top:10px;border-top:1px solid rgba(34,39,46,.12);',
};

export function GuidePicker({ G, own }: { G: GuideData; own: string }) {
  const { role } = useConsoleRole();
  const [pq, setPq] = useState('');
  const names = guideNames(G);
  const words = pq.trim().toLowerCase().split(/\s+/).filter((w) => w.length > 1);
  const hits = crossRoleSearch(G, pq, role);
  const card = (r: string) => pickerCard(G, TUTORIALS, r);
  const isBig = (r: string) => !!(G.roles[G.alias[r] || r] || { big: false }).big;
  const forHref = (r: string) => `/guide?for=${encodeURIComponent(r)}`;
  return (
    <div data-screen-label="Guide · pick role" style={sx(S.wrap)}>
      <div style={sx(S.col)}>
        <div style={sx(S.top)}>
          <span style={sx(S.logo)}>aithora</span>
          <span style={sx(S.kick)}>Guide</span>
          <span style={sx('flex:1 1 20px;')} />
          <Link to="/" className="g-back" style={sx(S.back)}>
            {'← Back to console'}
          </Link>
        </div>
        <div style={sx(S.hero)}>
          <div style={sx(S.h)}>Who are you?</div>
          <div style={sx(S.sub)}>Pick one. The guide shows only your steps and your answers, and every answer opens the right page.</div>
          <Link to={forHref(own)} className="g-cont" style={sx(S.last)}>
            <span>{`Continue as ${own}`}</span> <span style={sx('font-size:17px;')}>{'→'}</span>
          </Link>
          <div style={sx(S.searchWrap)}>
            <div className="g-pbox" style={sx(S.box)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#54606C" strokeWidth="2" style={sx('flex:0 0 18px;')} aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-4-4" />
              </svg>
              <input value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Or just ask: how do I snooze a task?" aria-label="Search every answer" style={sx(S.input)} />
              {words.length > 0 && (
                <button type="button" className="dlink" style={sx(S.clear)} onClick={() => setPq('')}>
                  Clear
                </button>
              )}
            </div>
            {words.length > 0 && (
              <div style={sx(S.res)}>
                <div style={sx(S.resHead)}>{`${hits.length}${hits.length === 1 ? ' answer' : ' answers'}`}</div>
                {hits.slice(0, 8).map((h) => (
                  <Link key={`${h.who}:${h.q}`} to={h.href} className="g-hit" style={sx(S.hit)}>
                    <span style={sx(S.hitTop)}>
                      <span style={sx('font-size:16px;font-weight:700;')}>{h.q}</span>
                      <span style={sx('font-size:14px;font-weight:700;color:#2F5C97;')}>{h.who}</span>
                      <span style={sx('flex:1;')} />
                      <span style={sx('font-size:16px;font-weight:700;color:#2F5C97;')}>{'Open →'}</span>
                    </span>
                    <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;')}>{h.a}</span>
                  </Link>
                ))}
                {hits.length === 0 && <div style={sx(S.none)}>No answer matches yet. Try fewer words, or pick your role below.</div>}
              </div>
            )}
          </div>
        </div>
        <div style={sx(S.sec(0.06))}>
          <div style={sx(S.secHead('#2F5C97'))}>
            <span style={sx(S.secK('#2F5C97'))}>At work</span>
            <span style={sx(S.secN)}>Staff and the Aithora team</span>
          </div>
          <div style={sx(S.grid)}>
            {names.filter((r) => !isBig(r)).map((r) => {
              const c = card(r);
              return (
                <Link key={r} to={forHref(r)} className="g-card" style={sx(S.card)}>
                  <span style={sx('display:flex;align-items:center;gap:8px;')}>
                    <span style={sx('font-size:19px;font-weight:800;letter-spacing:-.015em;')}>{c.label}</span>
                    <span style={sx('flex:1;')} />
                    <span style={sx('font-size:18px;font-weight:800;color:#2F5C97;')}>{'→'}</span>
                  </span>
                  <span style={sx('font-size:14px;color:#3C454F;line-height:1.45;text-wrap:pretty;flex:1 1 auto;')}>{c.lead}</span>
                  <span style={sx('font-size:14px;font-weight:700;color:#54606C;font-variant-numeric:tabular-nums;')}>{c.meta}</span>
                </Link>
              );
            })}
          </div>
        </div>
        <div style={sx(S.sec(0.12))}>
          <div style={sx(S.secHead('#1F6B52'))}>
            <span style={sx(S.secK('#1F6B52'))}>For you and your family</span>
            <span style={sx(S.secN)}>Large text, one step at a time</span>
          </div>
          <div style={sx(S.grid)}>
            {names.filter(isBig).map((r) => {
              const c = card(r);
              return (
                <Link key={r} to={forHref(r)} className="g-cardh" style={sx(S.cardH)}>
                  <span style={sx('display:flex;align-items:center;gap:8px;')}>
                    <span style={sx('font-size:22px;font-weight:800;letter-spacing:-.015em;')}>{c.label}</span>
                    <span style={sx('flex:1;')} />
                    <span style={sx('font-size:22px;font-weight:800;color:#1F6B52;')}>{'→'}</span>
                  </span>
                  <span style={sx('font-size:19px;color:#3C454F;line-height:1.4;text-wrap:pretty;flex:1 1 auto;')}>{c.lead}</span>
                </Link>
              );
            })}
          </div>
        </div>
        <div style={sx(S.foot)}>
          Not sure which you are? Ask whoever invited you, or email{' '}
          <a className="g-mail" href="mailto:info@aithoracare.com">
            info@aithoracare.com
          </a>
          .
        </div>
      </div>
    </div>
  );
}
